import { useState } from 'react';
import {
  AppleIcon,
  BrainIcon,
  CakeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  GaugeIcon,
  HeartIcon,
  MaximizeIcon,
  MoonIcon,
  PictureInPicture2Icon,
  PlayIcon,
  SearchIcon,
  SettingsIcon,
  SmileIcon,
  UserRoundIcon,
  Volume2Icon,
  WindIcon,
} from 'lucide-react';

import { TL, TlKaart, TlKnop, TlTerug, TlWrap } from './chrome.js';

/**
 * Programmabibliotheek van het nagebouwde Therapieland-zorgverlenerportaal:
 * bibliotheek -> programmadetail -> sessiepagina.
 *
 * Nagebouwd naar docs/designs/screenshots-therapieland/. Alle data is fictief
 * en hardcoded; er zijn geen netwerkcalls en geen videobestanden.
 */

interface TlProgramma {
  id: string;
  titel: string;
  doelgroepen: string[];
  /** Rondje-kleur uit de screenshots; buiten het TL-palet omdat elk programma zijn eigen merkkleur heeft. */
  kleur: string;
  Icon: typeof CakeIcon;
}

const PROGRAMMAS: TlProgramma[] = [
  {
    id: 'achttien',
    titel: '18! En nu?',
    doelgroepen: ['Volwassenen', 'Jongeren'],
    kleur: '#d93b95',
    Icon: CakeIcon,
  },
  {
    id: 'geluk',
    titel: '30 minuten: Aandacht voor geluk',
    doelgroepen: ['Volwassenen'],
    kleur: '#1b7f96',
    Icon: SmileIcon,
  },
  {
    id: 'leefstijl',
    titel: '30 minuten: Je leefstijl aanpassen',
    doelgroepen: ['Volwassenen'],
    kleur: '#4a9e4a',
    Icon: AppleIcon,
  },
  {
    id: 'slapen',
    titel: 'Beter slapen',
    doelgroepen: ['Volwassenen'],
    kleur: TL.primair,
    Icon: MoonIcon,
  },
  {
    id: 'angst',
    titel: 'Omgaan met angst en paniek',
    doelgroepen: ['Volwassenen', 'Jongeren'],
    kleur: '#1b7f96',
    Icon: WindIcon,
  },
  {
    id: 'piekeren',
    titel: 'Piekeren en malen',
    doelgroepen: ['Volwassenen', 'Jongeren'],
    kleur: TL.oranje,
    Icon: BrainIcon,
  },
  {
    id: 'mindfulness',
    titel: 'Mindfulness in de praktijk',
    doelgroepen: ['Volwassenen'],
    kleur: TL.groen,
    Icon: HeartIcon,
  },
];

const THEMAS = [
  'Aandacht en concentratie',
  'Angst en paniek',
  'Autisme',
  'Balans en herstel',
  'Coronavirus',
  'Dwang',
  'Eetproblematiek',
  'Emoties en stemming',
  'Identiteit en zingeving',
  'Leefstijl',
  'Levensfase',
  'Lichamelijke klachten en ziekten',
  'Mindset en positiviteit',
  'Neurocognitieve stoornis',
  'Opvoeden',
];

const SESSIES = [
  { id: 'intro', titel: 'Voordat je begint #intro' },
  { id: 'regelzaken', titel: 'Level up! #regelzaken' },
  { id: 'geldzaken', titel: 'Op je money #geldzaken' },
  { id: 'zelfstandigheid', titel: 'New beginnings #zelfstandigheid' },
];

const DOELGROEP_FILTERS = ['Kinderen', 'Jongeren', 'Volwassenen'];

type TlScherm = 'bibliotheek' | 'detail' | 'sessie';

export function TlProgrammas(): JSX.Element {
  const [scherm, setScherm] = useState<TlScherm>('bibliotheek');
  const [programmaId, setProgrammaId] = useState<string>(PROGRAMMAS[0].id);
  const [sessieId, setSessieId] = useState<string>(SESSIES[0].id);

  const programma = PROGRAMMAS.find((p) => p.id === programmaId) ?? PROGRAMMAS[0]!;

  if (scherm === 'detail') {
    return (
      <Detail
        programma={programma}
        opTerug={() => setScherm('bibliotheek')}
        opSessie={(id) => {
          setSessieId(id);
          setScherm('sessie');
        }}
      />
    );
  }

  if (scherm === 'sessie') {
    return (
      <Sessie
        programma={programma}
        sessieId={sessieId}
        opTerug={() => setScherm('detail')}
        opSessie={setSessieId}
      />
    );
  }

  return (
    <Bibliotheek
      opProgramma={(id) => {
        setProgrammaId(id);
        setScherm('detail');
      }}
    />
  );
}

function Bibliotheek({ opProgramma }: { opProgramma: (id: string) => void }): JSX.Element {
  const [themaZoek, setThemaZoek] = useState('');
  const [programmaZoek, setProgrammaZoek] = useState('');
  const [gekozenThemas, setGekozenThemas] = useState<string[]>([]);

  const zichtbareThemas = THEMAS.filter((t) =>
    t.toLowerCase().includes(themaZoek.trim().toLowerCase()),
  );

  return (
    <TlWrap>
      <h1 className="text-[34px] font-semibold" style={{ color: TL.ink }}>
        Programma&apos;s
      </h1>
      <p className="mt-6 text-[15.5px]" style={{ color: TL.body }}>
        Op deze pagina kun je programma&apos;s bekijken en toevoegen aan een werkmap.
      </p>

      <div className="mt-10 flex items-start gap-7">
        <TlKaart className="w-[304px] shrink-0 px-4 py-5">
          <h2 className="text-[21px] font-semibold" style={{ color: TL.ink }}>
            Thema selectie
          </h2>
          <p className="mt-1 text-[14.5px]" style={{ color: TL.body }}>
            Vind passende programma&apos;s
          </p>
          <input
            type="text"
            value={themaZoek}
            onChange={(e) => setThemaZoek(e.target.value)}
            placeholder="Zoek thema's"
            className="mt-4 w-full rounded px-3 py-2.5 text-[15px] outline-none"
            style={{ border: `1px solid ${TL.rand}`, color: TL.body }}
          />
          <ul className="mt-4 space-y-1">
            {zichtbareThemas.map((thema) => {
              const aan = gekozenThemas.includes(thema);
              return (
                <li key={thema}>
                  <button
                    type="button"
                    onClick={() =>
                      setGekozenThemas((vorige) =>
                        aan ? vorige.filter((t) => t !== thema) : [...vorige, thema],
                      )
                    }
                    className="flex w-full items-center gap-3 py-1.5 text-left"
                  >
                    <span
                      className="flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[2px]"
                      style={{
                        border: `1px solid ${aan ? TL.primair : TL.zacht}`,
                        backgroundColor: aan ? TL.primair : '#fff',
                      }}
                    >
                      {aan ? <CheckMarkje /> : null}
                    </span>
                    <span className="text-[15px]" style={{ color: TL.ink }}>
                      {thema}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </TlKaart>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-6">
            <div className="flex" style={{ border: `1px solid ${TL.rand}` }}>
              <input
                type="text"
                value={programmaZoek}
                onChange={(e) => setProgrammaZoek(e.target.value)}
                placeholder="Zoek programma's"
                className="w-[230px] px-3 py-2.5 text-[15px] outline-none"
                style={{ color: TL.body }}
              />
              <button
                type="button"
                className="flex w-[52px] items-center justify-center"
                style={{ borderLeft: `1px solid ${TL.rand}`, backgroundColor: TL.panel }}
                aria-label="Zoek programma's"
              >
                <SearchIcon size={19} style={{ color: TL.ink }} />
              </button>
            </div>

            <span className="text-[15.5px]" style={{ color: TL.ink }}>
              Totaal: 221
            </span>

            <span
              className="flex items-center gap-6 rounded px-4 py-2.5 text-[15.5px]"
              style={{ border: `1px solid ${TL.rand}`, color: TL.ink }}
            >
              Toon 10 resultaten
              <ChevronDownIcon size={20} style={{ color: TL.ink }} />
            </span>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <span className="text-[15.5px] font-semibold" style={{ color: TL.ink }}>
              Toon alleen
            </span>
            {DOELGROEP_FILTERS.map((d) => (
              <span
                key={d}
                className="rounded px-3 py-1.5 text-[14px]"
                style={{ backgroundColor: TL.chip, color: TL.body }}
              >
                {d}
              </span>
            ))}
          </div>

          <div className="mt-6 space-y-6">
            {PROGRAMMAS.map((p) => (
              <TlKaart key={p.id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => opProgramma(p.id)}
                  className="w-full text-left"
                >
                  <div
                    className="flex items-center gap-5 px-6 py-5"
                    style={{ backgroundColor: TL.panel }}
                  >
                    <ProgrammaIcoon programma={p} grootte={48} />
                    <span className="text-[22px] font-semibold" style={{ color: TL.ink }}>
                      {p.titel}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-5 px-6 py-5">
                    <span>
                      <span className="block text-[15.5px]" style={{ color: TL.body }}>
                        Open het programma om de inhoud te bekijken
                      </span>
                      <span className="mt-3 flex gap-2">
                        {p.doelgroepen.map((d) => (
                          <Chip key={d}>{d}</Chip>
                        ))}
                      </span>
                    </span>
                    <ChevronRightIcon size={30} strokeWidth={1.6} style={{ color: TL.ink }} />
                  </div>
                </button>
              </TlKaart>
            ))}
          </div>
        </div>
      </div>
    </TlWrap>
  );
}

function Detail({
  programma,
  opTerug,
  opSessie,
}: {
  programma: TlProgramma;
  opTerug: () => void;
  opSessie: (id: string) => void;
}): JSX.Element {
  return (
    <TlWrap>
      <TlTerug label="Programma's" onClick={opTerug} />

      <div className="mt-8 flex items-center gap-5">
        <ProgrammaIcoon programma={programma} grootte={64} />
        <h1 className="text-[32px] font-semibold" style={{ color: TL.ink }}>
          {programma.titel}
        </h1>
      </div>

      <div className="mt-9 h-px" style={{ backgroundColor: TL.rand }} />

      <div className="mt-9 flex items-start gap-10">
        <div className="min-w-0 flex-1">
          <h2 className="text-[25px] font-semibold" style={{ color: TL.ink }}>
            Programma introductie
          </h2>
          <span className="mt-4 block text-[15.5px]" style={{ color: TL.primair }}>
            Direct naar programma inhoud
          </span>

          <h3 className="mt-12 text-[21px] font-semibold" style={{ color: TL.groenInk }}>
            Doel van het programma
          </h3>
          <h4 className="mt-6 text-[16px]" style={{ color: TL.groenInk }}>
            Hoofddoel
          </h4>
          <p className="mt-4 text-[15.5px] leading-7" style={{ color: TL.body }}>
            Jongeren zelfredzaam maken rondom regelzaken die komen kijken bij de overgang naar het
            volwassen leven.
          </p>
          <h4 className="mt-6 text-[16px]" style={{ color: TL.groenInk }}>
            Subdoelen
          </h4>

          <div className="mt-8 flex justify-center">
            <TlKnop variant="omlijnd">Toon meer</TlKnop>
          </div>
        </div>

        <div className="w-[220px] shrink-0 px-5 py-5" style={{ backgroundColor: TL.panel }}>
          <h3 className="text-[16px] font-semibold" style={{ color: TL.ink }}>
            Geschikt voor
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {programma.doelgroepen.map((d) => (
              <Chip key={d}>{d}</Chip>
            ))}
          </div>

          <h3 className="mt-8 text-[16px] font-semibold" style={{ color: TL.ink }}>
            Over het programma
          </h3>
          <div className="mt-3 space-y-3">
            {['Overzicht van het programma', 'Literatuur', 'Analyse van de doelgroep'].map((l) => (
              <span key={l} className="block text-[15px] leading-6" style={{ color: TL.primair }}>
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14">
        <div className="flex items-center justify-between gap-6">
          <h2 className="text-[25px] font-semibold" style={{ color: TL.ink }}>
            Programma inhoud
          </h2>
          <span
            className="flex items-center gap-2 text-[13px] tracking-wide"
            style={{ color: TL.muted }}
          >
            ALLES UITKLAPPEN
            <ChevronDownIcon size={19} style={{ color: TL.muted }} />
          </span>
        </div>

        <div className="mt-6 space-y-4">
          {SESSIES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => opSessie(s.id)}
              className="flex w-full items-center justify-between gap-5 rounded px-6 py-5 text-left"
              style={{ backgroundColor: TL.panel, border: `1px solid ${TL.rand}` }}
            >
              <span className="text-[17.5px] font-semibold" style={{ color: TL.ink }}>
                {s.titel}
              </span>
              <ChevronDownIcon size={22} strokeWidth={1.7} style={{ color: TL.ink }} />
            </button>
          ))}
        </div>
      </div>
    </TlWrap>
  );
}

function Sessie({
  programma,
  sessieId,
  opTerug,
  opSessie,
}: {
  programma: TlProgramma;
  sessieId: string;
  opTerug: () => void;
  opSessie: (id: string) => void;
}): JSX.Element {
  return (
    <TlWrap>
      <TlTerug label={programma.titel} onClick={opTerug} />

      <h1 className="mt-5 text-[30px] font-semibold" style={{ color: TL.ink }}>
        {programma.titel}
      </h1>

      <div className="mt-8 flex items-start gap-10">
        <div className="min-w-0 flex-1">
          <h2 className="text-[20px] font-semibold" style={{ color: TL.ink }}>
            Introductie
          </h2>
          <p className="mt-7 text-[15.5px] leading-8" style={{ color: TL.body }}>
            Als je officieel volwassen bent, ziet de overheid je als iemand die zich zelfstandig zou
            moeten kunnen redden. En dit betekent dat er voor jou heel wat dingen gaan veranderen.
            In dit programma word je meegenomen langs deze veranderingen. Je leert meer over wat er
            van je verwacht wordt, maar ook waar je recht op hebt. Volwassen worden brengt namelijk
            ook veel voordelen met zich mee!
          </p>

          <VideoSpeler />

          <div
            className="flex items-center justify-between gap-6 px-6 py-4"
            style={{ borderBottom: `1px solid ${TL.rand}` }}
          >
            <span className="flex items-center gap-9">
              {['Samenvatting', 'Tekst'].map((l) => (
                <span
                  key={l}
                  className="flex items-center gap-2 text-[15.5px]"
                  style={{ color: TL.primair }}
                >
                  {l}
                  <ChevronDownIcon size={18} style={{ color: TL.primair }} />
                </span>
              ))}
            </span>
            <span className="flex gap-3">
              <SessieAvatar />
              <SessieAvatar />
            </span>
          </div>

          <h2 className="mt-10 text-[21px] font-semibold" style={{ color: TL.groenInk }}>
            Na dit programma
          </h2>
        </div>

        <div className="w-[240px] shrink-0">
          <h2 className="text-[20px] font-semibold" style={{ color: TL.ink }}>
            Programma overzicht
          </h2>
          <div
            className="mt-5 rounded px-4 py-4"
            style={{ backgroundColor: TL.panel, border: `1px solid ${TL.rand}` }}
          >
            {SESSIES.map((s, i) => {
              const open = s.id === sessieId;
              return (
                <div key={s.id}>
                  <button
                    type="button"
                    onClick={() => opSessie(s.id)}
                    className="flex w-full items-start gap-3 py-2 text-left"
                  >
                    <Statusbolletje grootte={16} />
                    <span
                      className="flex-1 text-[13.5px] font-semibold leading-5"
                      style={{ color: TL.ink }}
                    >
                      Sessie {i + 1}: {s.titel}
                    </span>
                    {open ? (
                      <ChevronUpIcon size={17} strokeWidth={1.8} style={{ color: TL.ink }} />
                    ) : (
                      <ChevronDownIcon size={17} strokeWidth={1.8} style={{ color: TL.ink }} />
                    )}
                  </button>
                  {open ? (
                    <span className="flex items-center gap-3 py-2 pl-7">
                      <Statusbolletje grootte={14} />
                      <span className="text-[13.5px] tracking-wide" style={{ color: TL.muted }}>
                        Introductie
                      </span>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TlWrap>
  );
}

/** Er is geen videobestand in de demo; dit blok bootst alleen de speler na. */
function VideoSpeler(): JSX.Element {
  return (
    <div
      className="relative mt-8 flex aspect-video items-center justify-center"
      style={{ backgroundColor: TL.primair }}
    >
      <span className="px-10 text-center text-[38px] font-semibold leading-tight text-white">
        Waarom dit programma?
      </span>

      <div
        className="absolute inset-x-0 bottom-0 flex items-center gap-4 px-3 py-3"
        style={{ backgroundColor: TL.ink }}
      >
        <span className="flex h-[30px] w-[54px] items-center justify-center bg-white">
          <PlayIcon size={16} style={{ color: TL.ink, fill: TL.ink }} />
        </span>
        <span className="relative h-[4px] flex-1" style={{ backgroundColor: TL.muted }}>
          <span
            className="absolute inset-y-0 left-0 w-[8%]"
            style={{ backgroundColor: TL.zacht }}
          />
          <span
            className="absolute -top-6 left-0 rounded px-1.5 py-0.5 text-[11px] font-semibold"
            style={{ backgroundColor: '#fff', color: TL.ink }}
          >
            01:06
          </span>
        </span>
        <span className="flex items-center gap-4 pr-1">
          <Volume2Icon size={18} className="text-white" />
          <SettingsIcon size={18} className="text-white" />
          <GaugeIcon size={18} className="text-white" />
          <PictureInPicture2Icon size={18} className="text-white" />
          <MaximizeIcon size={18} className="text-white" />
        </span>
      </div>
    </div>
  );
}

function ProgrammaIcoon({
  programma,
  grootte,
}: {
  programma: TlProgramma;
  grootte: number;
}): JSX.Element {
  const { Icon, kleur } = programma;
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: kleur, width: grootte, height: grootte }}
    >
      <Icon size={grootte * 0.5} strokeWidth={1.8} className="text-white" />
    </span>
  );
}

function Chip({ children }: { children: string }): JSX.Element {
  return (
    <span
      className="rounded px-2.5 py-1 text-[13px]"
      style={{ border: `1px solid ${TL.rand}`, backgroundColor: '#fff', color: TL.body }}
    >
      {children}
    </span>
  );
}

function Statusbolletje({ grootte }: { grootte: number }): JSX.Element {
  return (
    <span
      className="mt-0.5 shrink-0 rounded-full"
      style={{ width: grootte, height: grootte, border: `1.5px solid ${TL.ink}` }}
    />
  );
}

function SessieAvatar(): JSX.Element {
  return (
    <span
      className="flex h-[38px] w-[38px] items-center justify-center rounded-full"
      style={{ backgroundColor: TL.chip, border: `1px solid ${TL.primair}` }}
    >
      <UserRoundIcon size={20} style={{ color: TL.primair }} />
    </span>
  );
}

function CheckMarkje(): JSX.Element {
  return (
    <span
      className="block h-[7px] w-[4px] rotate-45"
      style={{ borderBottom: '2px solid #fff', borderRight: '2px solid #fff' }}
    />
  );
}
